import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export type GoogleSearchFeedSection = 'videos' | 'short_videos' | 'news';

export type GoogleSearchFeedPlatform =
  | 'google'
  | 'youtube'
  | 'tiktok'
  | 'instagram'
  | 'facebook'
  | 'other';

export interface GoogleSearchFeedItem {
  id: string;
  query: string;
  section: GoogleSearchFeedSection;
  platform: GoogleSearchFeedPlatform;
  url: string;
  title: string;
  snippet: string | null;
  source: string | null;
  publishedText: string | null;
  publishedAt: string | null;
  thumbnailUrl: string | null;
  rank: number | null;
  firstSeenRank: number | null;
  firstSeenAt: string | null;
  lastSeenAt: string | null;
  seenCount: number;
}

export interface GoogleSearchFeedResponse {
  query: string;
  slug: string;
  sections: GoogleSearchFeedSection[];
  platform: GoogleSearchFeedPlatform | 'all';
  platformCounts: Record<GoogleSearchFeedPlatform, number>;
  sectionCounts: {
    all: number;
    videos: number;
    short_videos: number;
    news: number;
  };
  lastUpdatedAt: string | null;
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  hasMore: boolean;
  items: GoogleSearchFeedItem[];
}

export type GoogleSearchFeedSectionFilter = 'all' | GoogleSearchFeedSection;
export type GoogleSearchFeedPlatformFilter = 'all' | GoogleSearchFeedPlatform;

const EMPTY_PLATFORM_COUNTS: Record<GoogleSearchFeedPlatform, number> = {
  google: 0,
  youtube: 0,
  tiktok: 0,
  instagram: 0,
  facebook: 0,
  other: 0,
};

export function classifyFeedPlatform(
  url: string,
  section: GoogleSearchFeedSection,
): GoogleSearchFeedPlatform {
  if (section === 'news') return 'google';

  try {
    const host = new URL(url).hostname.toLowerCase().replace(/^www\./, '');
    if (host === 'youtube.com' || host === 'youtu.be' || host === 'm.youtube.com') return 'youtube';
    if (host === 'tiktok.com' || host.endsWith('.tiktok.com')) return 'tiktok';
    if (host === 'instagram.com' || host.endsWith('.instagram.com')) return 'instagram';
    if (
      host === 'facebook.com' ||
      host === 'fb.com' ||
      host === 'fb.watch' ||
      host.endsWith('.facebook.com')
    ) {
      return 'facebook';
    }
  } catch {
    /* ignore */
  }

  return 'other';
}

function normalizeItem(item: GoogleSearchFeedItem): GoogleSearchFeedItem {
  const platform =
    item.platform ?? classifyFeedPlatform(item.url, item.section);
  return { ...item, platform };
}

function buildPlatformCounts(
  items: GoogleSearchFeedItem[],
): Record<GoogleSearchFeedPlatform, number> {
  return items.reduce(
    (counts, item) => {
      counts[item.platform] = (counts[item.platform] ?? 0) + 1;
      return counts;
    },
    { ...EMPTY_PLATFORM_COUNTS },
  );
}

function normalizeResponse(data: GoogleSearchFeedResponse): GoogleSearchFeedResponse {
  const items = (data.items ?? []).map(normalizeItem);
  const platformCounts = data.platformCounts ?? buildPlatformCounts(items);
  const sectionCounts = data.sectionCounts ?? {
    all: data.total ?? items.length,
    videos: items.filter((item) => item.section === 'videos').length,
    short_videos: items.filter((item) => item.section === 'short_videos').length,
    news: items.filter((item) => item.section === 'news').length,
  };
  const total = data.total ?? items.length;
  const page = data.page ?? 1;
  const limit = data.limit ?? 20;
  const totalPages = data.totalPages ?? Math.max(1, Math.ceil(total / limit));
  const hasMore = data.hasMore ?? page < totalPages;

  return {
    ...data,
    items,
    platformCounts,
    sectionCounts,
    total,
    page,
    limit,
    totalPages,
    hasMore,
  };
}

export async function fetchPublicGoogleSearch(
  slug: string,
  section: GoogleSearchFeedSectionFilter = 'all',
  platform: GoogleSearchFeedPlatformFilter = 'all',
  page = 1,
  limit = 20,
): Promise<GoogleSearchFeedResponse> {
  const params: Record<string, string | number> = {
    page,
    limit,
  };
  if (section !== 'all') params.section = section;
  if (platform !== 'all') params.platform = platform;

  const { data } = await axios.get<GoogleSearchFeedResponse>(
    `${API_BASE_URL}/api/public/google-search/${encodeURIComponent(slug)}`,
    { params },
  );

  return normalizeResponse(data);
}
